import { useRef, useLayoutEffect, useCallback } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowUpRight, RotateCw, Sparkles, CheckCircle2, MapPin, Building2 } from 'lucide-react';
import { PROJECTS, UPCOMING, PLOT_DEVELOPMENTS } from '@/data/content';
import { SplitLines } from '@/components/SplitLines';
import { scrollToId } from '@/lib/smoothScroll';

gsap.registerPlugin(ScrollTrigger);

/* ─────────────────────────────────────────────────────────
   Badge theme map
   ───────────────────────────────────────────────────────── */
const badgeStyle = {
  Completed: 'bg-[#0B1226]/85 text-[#F2CE6B] border-[#F2CE6B]/40 shadow-sm',
  Ongoing:   'bg-[#0B1226]/85 text-[#9DB4F5] border-[#9DB4F5]/40 shadow-sm',
  Upcoming:  'bg-[#0B1226]/85 text-[#8FD6A0] border-[#8FD6A0]/40 shadow-sm',
};

/* ─────────────────────────────────────────────────────────
   ProjectCard — Pure CSS 3D flip (GPU composited)
   ───────────────────────────────────────────────────────── */
function ProjectCard({ p }) {
  const cardRef = useRef(null);

  const handleFlip = useCallback((flipped) => {
    const el = cardRef.current;
    if (!el) return;
    if (flipped) {
      el.classList.add('is-flipped');
    } else {
      el.classList.remove('is-flipped');
    }
  }, []);

  const handleToggle = useCallback(() => {
    const el = cardRef.current;
    if (!el) return;
    el.classList.toggle('is-flipped');
  }, []);

  return (
    <article
      ref={cardRef}
      data-testid={`project-card-${p.id}`}
      className="project-card h-[400px] sm:h-[450px] lg:h-[480px] xl:h-[520px] w-full select-none touch-manipulation cursor-pointer group"
      onMouseEnter={() => handleFlip(true)}
      onMouseLeave={() => handleFlip(false)}
      onClick={handleToggle}
    >
      <div className="card-inner">
        {/* ═══════ FRONT FACE ═══════ */}
        <div className="card-face card-front rounded-2xl border border-ink/15 overflow-hidden bg-[#0B1226]">
          {/* Ambient blurred backdrop */}
          <img
            src={p.img}
            alt=""
            className="absolute inset-0 w-full h-full object-cover blur-xl opacity-35 scale-110 pointer-events-none"
          />

          {/* Primary image */}
          <img
            src={p.img}
            alt={`${p.name} — ${p.location}`}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-contain p-4 pb-24 pointer-events-none z-0 card-image"
          />

          {/* Gradient overlays */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1226] via-[#0B1226]/30 to-transparent opacity-85 pointer-events-none z-[1]" />
          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-[radial-gradient(ellipse_60%_50%_at_50%_40%,rgba(242,206,107,0.12),transparent_70%)] pointer-events-none z-[1]" />

          {/* Top Left Badge */}
          <span className={`absolute top-4 left-4 text-[10px] uppercase tracking-[0.2em] border px-3 py-1 rounded-full font-semibold z-10 ${badgeStyle[p.status]}`}>
            {p.status}
          </span>

          {/* Flip Indicator */}
          <div className="absolute top-4 right-4 z-10 flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/60 border border-white/15 text-white/90 group-hover:border-[#F2CE6B]/60 group-hover:text-[#F2CE6B] transition-all duration-300 shadow-sm">
            <span className="text-[10px] uppercase tracking-wider font-semibold">Flip Details</span>
            <RotateCw size={12} className="flip-icon" />
          </div>

          {/* Bottom Content */}
          <div className="absolute bottom-0 inset-x-0 p-5 z-10 bg-gradient-to-t from-[#0B1226] via-[#0B1226]/95 to-transparent pt-8">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#F2CE6B] font-semibold flex items-center gap-1">
              <MapPin size={12} className="inline shrink-0" />
              {p.location}
            </p>
            <h3 className="mt-1 font-clash font-semibold text-xl sm:text-2xl text-white tracking-tight leading-snug truncate">{p.name}</h3>
            <div className="mt-2 flex flex-wrap items-center gap-x-2.5 gap-y-1 text-xs text-white/80">
              <span className="flex items-center gap-1">
                <Building2 size={12} className="text-[#F2CE6B]/80 shrink-0" />
                {p.type}
              </span>
              <span className="w-1 h-1 rounded-full bg-white/40" />
              <span className="text-white/95 font-medium">{p.meta}</span>
            </div>
          </div>
        </div>

        {/* ═══════ BACK FACE ═══════ */}
        <div className="card-face card-back rounded-2xl border border-[#F2CE6B]/40 bg-gradient-to-br from-[#0E162B] via-[#0B1124] to-[#070B18] p-5 flex flex-col justify-between overflow-hidden shadow-2xl">
          {/* Decorative ambient glow */}
          <div className="absolute top-0 right-0 w-48 h-48 bg-[radial-gradient(circle,rgba(242,206,107,0.08),transparent_70%)] pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-56 h-56 bg-[radial-gradient(circle,rgba(30,58,123,0.1),transparent_70%)] pointer-events-none" />

          {/* Back Header */}
          <div className="relative z-10 flex items-center justify-between gap-2 border-b border-white/15 pb-2.5">
            <div className="flex items-center gap-2">
              <span className={`text-[9px] uppercase tracking-[0.15em] border px-2.5 py-1 rounded-full font-semibold ${badgeStyle[p.status]}`}>
                {p.status}
              </span>
              <span className="text-[10px] uppercase tracking-wider text-[#F2CE6B] font-semibold flex items-center gap-1 truncate">
                <MapPin size={11} className="shrink-0" />
                {p.location}
              </span>
            </div>
            <span className="text-[10px] text-white/60 flex items-center gap-1 shrink-0 font-mono">
              <RotateCw size={11} className="text-[#F2CE6B]" /> Flipped
            </span>
          </div>

          {/* Back Details Main Content */}
          <div className="relative z-10 my-auto space-y-3 py-1">
            <div>
              <h3 className="font-clash font-semibold text-xl sm:text-2xl text-white tracking-tight truncate">
                {p.name}
              </h3>
              <p className="text-xs text-[#F2CE6B] font-medium mt-0.5">{p.type}</p>
            </div>

            {/* Key Spec Badges */}
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-white/5 border border-white/10 rounded-xl p-2.5">
                <p className="text-[9px] text-white/50 uppercase tracking-wider font-mono">Scale / Footprint</p>
                <p className="font-semibold text-white mt-0.5 text-xs truncate">{p.meta}</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-xl p-2.5">
                <p className="text-[9px] text-white/50 uppercase tracking-wider font-mono">Delivery Status</p>
                <p className="font-semibold text-[#F2CE6B] mt-0.5 text-xs truncate">
                  {p.status === 'Completed' ? '100% Delivered' : p.status === 'Upcoming' ? 'Planning Phase' : 'Active Development'}
                </p>
              </div>
            </div>

            {/* Highlights List */}
            <div className="space-y-1">
              <p className="text-[9px] uppercase tracking-wider text-white/50 font-mono flex items-center gap-1">
                <Sparkles size={11} className="text-[#F2CE6B]" /> Key Highlights
              </p>
              <ul className="space-y-1 text-xs text-white/85">
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={12} className="text-[#F2CE6B] shrink-0" />
                  <span className="line-clamp-1">Strategic high-growth real estate corridor</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={12} className="text-[#F2CE6B] shrink-0" />
                  <span className="line-clamp-1">100% Clear title &amp; master-planned infrastructure</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 size={12} className="text-[#F2CE6B] shrink-0" />
                  <span className="line-clamp-1">Delivered to highest architectural standards</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Back Action Footer */}
          <div className="relative z-10 pt-2.5 border-t border-white/15 flex items-center justify-between">
            <span className="text-[10px] text-white/50 uppercase tracking-widest font-mono">Jupally Avenues</span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                scrollToId('contact');
              }}
              className="text-xs font-semibold text-[#F2CE6B] hover:text-white bg-[#F2CE6B]/15 hover:bg-[#F2CE6B]/30 border border-[#F2CE6B]/40 px-3.5 py-1 rounded-full transition-all duration-300 flex items-center gap-1.5 shadow-md"
            >
              Enquire <ArrowUpRight size={13} />
            </button>
          </div>
        </div>
      </div>
    </article>
  );
}

/* ─────────────────────────────────────────────────────────
   Section Header — Staggered entrance
   ───────────────────────────────────────────────────────── */
function SectionHeader({ dotColor, title }) {
  const ref = useRef(null);

  useLayoutEffect(() => {
    if (!ref.current) return;
    const els = ref.current.querySelectorAll('.gsap-reveal');
    gsap.set(els, { opacity: 0, y: 30, x: -10 });

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: ref.current,
        start: 'top 85%',
        once: true,
        onEnter: () => {
          gsap.to(els, {
            opacity: 1,
            y: 0,
            x: 0,
            duration: 0.9,
            ease: 'power3.out',
            stagger: 0.08,
          });
        },
      });
    }, ref);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={ref} className="flex items-center gap-3 mb-8">
      <span className={`gsap-reveal w-2.5 h-2.5 rounded-full ${dotColor}`} />
      <h3 className="gsap-reveal font-clash font-semibold text-ink tracking-tight text-2xl md:text-3xl">
        {title}
      </h3>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Project Grid — GSAP ScrollTrigger batch stagger
   ───────────────────────────────────────────────────────── */
function ProjectGrid({ projects, testId }) {
  const gridRef = useRef(null);

  useLayoutEffect(() => {
    if (!gridRef.current) return;
    const cards = gridRef.current.querySelectorAll('.project-card');
    if (!cards.length) return;

    gsap.set(cards, { opacity: 0, y: 80, scale: 0.92, rotateX: -6 });

    const ctx = gsap.context(() => {
      ScrollTrigger.batch(cards, {
        onEnter: (batch) => {
          gsap.to(batch, {
            opacity: 1,
            y: 0,
            scale: 1,
            rotateX: 0,
            duration: 1.1,
            ease: 'power4.out',
            stagger: 0.12,
            overwrite: true,
          });
        },
        start: 'top 88%',
        once: true,
      });
    }, gridRef);

    return () => ctx.revert();
  }, [projects]);

  return (
    <div
      ref={gridRef}
      data-testid={testId}
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 w-full"
      style={{ perspective: '1200px' }}
    >
      {projects.map((p) => (
        <ProjectCard key={p.id} p={p} />
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Upcoming Grid — Intro card + project cards
   ───────────────────────────────────────────────────────── */
function UpcomingGrid() {
  const gridRef = useRef(null);

  useLayoutEffect(() => {
    if (!gridRef.current) return;
    const items = Array.from(gridRef.current.children);
    if (!items.length) return;

    gsap.set(items, { opacity: 0, y: 60, scale: 0.95 });

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: gridRef.current,
        start: 'top 85%',
        once: true,
        onEnter: () => {
          gsap.to(items, {
            opacity: 1,
            y: 0,
            scale: 1,
            duration: 1.0,
            ease: 'power4.out',
            stagger: 0.14,
          });
        },
      });
    }, gridRef);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={gridRef} className="grid grid-cols-1 lg:grid-cols-3 gap-6 w-full">
      {/* Intro Card */}
      <div
        data-testid="upcoming-projects-card"
        className="h-[400px] sm:h-[450px] lg:h-[480px] xl:h-[520px] w-full glass rounded-2xl p-6 sm:p-8 flex flex-col justify-between border border-ink/15 hover:border-[#8FD6A0]/50 transition-all duration-500 shadow-md group"
      >
        <div>
          <span className="inline-block text-[10px] uppercase tracking-[0.2em] border px-3 py-1.5 rounded-full bg-brand-green/10 text-brand-green border-brand-green/30 font-semibold mb-6">
            Upcoming
          </span>
          <h3 className="font-clash font-semibold text-2xl sm:text-3xl text-ink tracking-tight leading-tight">
            The next chapter is<br /> already being drawn.
          </h3>
          <p className="mt-4 text-xs sm:text-sm text-ink/70 leading-relaxed">
            Strategic locations under planning. We are expanding our footprint with premium developments designed for future living.
          </p>
        </div>
        <button
          data-testid="upcoming-enquire-btn"
          onClick={() => scrollToId('contact')}
          className="self-start text-sm font-semibold text-brand-gold flex items-center gap-2 hover:gap-3 transition-[gap] duration-300 pt-2"
        >
          Register early interest <ArrowUpRight size={16} />
        </button>
      </div>

      {/* Project Cards */}
      {UPCOMING.map((p) => (
        <ProjectCard key={p.id} p={p} />
      ))}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   Plot Development Header
   ───────────────────────────────────────────────────────── */
function PlotHeader() {
  const ref = useRef(null);

  useLayoutEffect(() => {
    if (!ref.current) return;
    const els = ref.current.querySelectorAll('.gsap-reveal');
    gsap.set(els, { opacity: 0, y: 40 });

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: ref.current,
        start: 'top 85%',
        once: true,
        onEnter: () => {
          gsap.to(els, {
            opacity: 1,
            y: 0,
            duration: 1.0,
            ease: 'power3.out',
            stagger: 0.12,
          });
        },
      });
    }, ref);

    return () => ctx.revert();
  }, []);

  return (
    <div ref={ref} className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
      <div>
        <h3 className="gsap-reveal font-clash font-semibold text-ink tracking-tight text-3xl md:text-4xl">
          Completed Plot Developments
        </h3>
      </div>
      <div className="gsap-reveal glass px-6 py-3 rounded-full border border-ink/10 shadow-sm">
        <p className="text-sm font-medium text-ink">Total Developed: <span className="text-brand-gold font-bold">405+ Acres</span></p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   MAIN PROJECTS SECTION
   ───────────────────────────────────────────────────────── */
export const Projects = () => {
  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const ongoingProjects = PROJECTS.filter((p) => p.status === 'Ongoing');
  const completedProjects = PROJECTS.filter((p) => p.status === 'Completed');

  useLayoutEffect(() => {
    if (!headerRef.current) return;
    const els = headerRef.current.querySelectorAll('.gsap-header');
    gsap.set(els, { opacity: 0, y: 50 });

    const ctx = gsap.context(() => {
      ScrollTrigger.create({
        trigger: headerRef.current,
        start: 'top 80%',
        once: true,
        onEnter: () => {
          gsap.to(els, {
            opacity: 1,
            y: 0,
            duration: 1.2,
            ease: 'power4.out',
            stagger: 0.15,
          });
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="projects"
      data-testid="projects-section"
      className="relative bg-void section-pad py-28 md:py-40 max-w-7xl mx-auto overflow-hidden"
    >
      {/* Portfolio Header */}
      <div ref={headerRef} className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-20">
        <div>
          <p className="gsap-header text-[11px] uppercase tracking-[0.4em] text-brand-gold mb-5 font-semibold">03 — Portfolio</p>
          <h2 className="gsap-header font-clash font-semibold text-ink tracking-tight leading-[1.05] text-4xl md:text-5xl">
            <SplitLines inView lines={['Communities we have', <span key="b" className="text-ink/70">brought to life.</span>]} />
          </h2>
        </div>
        <p className="gsap-header max-w-xs text-xs md:text-sm text-ink/70 leading-relaxed">
          Over 405+ acres delivered across Hyderabad, Manikonda, Khajaguda and growth corridors — with active ongoing developments.
        </p>
      </div>

      {/* ─── 1. ONGOING DEVELOPMENTS ─── */}
      <div className="mb-16">
        <SectionHeader dotColor="bg-[#9DB4F5]" title="Ongoing Developments" />
        <ProjectGrid projects={ongoingProjects} testId="ongoing-grid" />
      </div>

      {/* ─── 2. UPCOMING DEVELOPMENTS ─── */}
      <div className="mb-20 border-t border-ink/10 pt-16">
        <SectionHeader dotColor="bg-[#8FD6A0]" title="Upcoming Developments" />
        <UpcomingGrid />
      </div>

      {/* ─── 3. COMPLETED LANDMARKS ─── */}
      <div className="mb-20 border-t border-ink/10 pt-16">
        <SectionHeader dotColor="bg-brand-gold" title="Completed Landmarks" />
        <ProjectGrid projects={completedProjects} testId="completed-grid" />
      </div>

      {/* ─── 4. COMPLETED PLOT DEVELOPMENTS ─── */}
      <div className="border-t border-ink/10 pt-16 md:pt-24">
        <PlotHeader />
        <ProjectGrid projects={PLOT_DEVELOPMENTS} testId="plot-grid" />
      </div>
    </section>
  );
};
